/**
 * IS THE HUB'S DATABASE DEAD, OR JUST WAITING ON THE NETWORK? (2026-11-01)
 *
 * THE INCIDENT. Bobbie, about 10:25am Central, on her iPhone, in the Home
 * Screen app, just back from another app: she tapped Understood/complete on
 * Ms. Kim's staff announcement and the button sat on "Saving..." for minutes.
 * Asked what happened next: "Still stuck". She swiped the app closed, opened
 * it again, tapped the same button, and "Yes, it saved" at once. Nothing on
 * the server changed in that window. The page's Firestore had died in the
 * background WITHOUT throwing anything, so the b815 recovery
 * (lib/firestoreClosedRecovery.ts), which only ever hears an error, heard
 * nothing: no bar, no reload.
 *
 * WHAT A SILENTLY DEAD FIRESTORE IS, read in the installed SDK
 * (@firebase/firestore 4.15.0, pinned by tests/firestore-stalled-recovery):
 *
 *   1. RESTRICTED MODE, the SDK's own doing. IndexedDbPersistence listens for
 *      'pagehide'. On a browser whose user agent says Safari and matches
 *      /(?:Version|Mobile)\/1[456]/, which every iPhone Safari user agent
 *      does through its frozen "Mobile/15E148", it calls
 *      enterRestrictedMode() on its work queue (AsyncQueueImpl). From then on
 *      every enqueue() "Return[s] a Promise which never resolves": every
 *      read, every write, every listener, forever, and nothing is thrown or
 *      logged. A page that comes back after that pagehide (the back/forward
 *      cache, a phone that hid it and showed it again) is a page whose
 *      Firestore can never answer again. The queue says so itself: its
 *      isShuttingDown getter is true. The only other way to set it is
 *      terminate(), which the Hub calls only on sign-out (lib/auth.ts), and
 *      the probe skips a terminated Firestore.
 *   2. A STORAGE THAT STOPPED ANSWERING. Firestore keeps its offline copy in
 *      IndexedDB and runs every step of its work one at a time on that same
 *      queue, so one IndexedDB request that never fires success or error
 *      holds every later Firestore call behind it, silently. The SDK checks
 *      the word Safari before it uses restricted mode, and a Home Screen
 *      app's user agent can leave that word out, so this second shape is the
 *      one the probe cannot skip.
 *
 * AND WHAT LOOKS THE SAME BUT IS NOT DEAD, also read in the installed SDK: a
 * runTransaction() commit runs ON the queue and the queue waits there for the
 * server's answer (TransactionRunner: enqueueAndForget(() => e.commit()...)).
 * On a network that hangs rather than fails, a cache read waits behind that
 * commit, on a Firestore that is alive (proven in the harness, scenario D).
 * "The cache did not answer" alone is therefore NEVER treated as proof of
 * death here. Slowness is never proof. Offline is not even slow: with every
 * request failing, the cache answers at once (harness, scenario C).
 *
 * SO THE PROBE PROVES DEATH TWO WAYS, BOTH ON THE PHONE, NEITHER ON THE
 * NETWORK, and anything else is not death:
 *   - the queue is in restricted mode (a flag read straight from the SDK);
 *   - one read from Firestore's on-device cache (getDocFromCache: no
 *     network, no rules, nothing billed) does not answer within
 *     CACHE_PROBE_TIMEOUT_MS, AND the browser's own IndexedDB, asked
 *     directly outside Firestore (two tiny requests, either answer counts),
 *     does not answer within STORAGE_PROBE_TIMEOUT_MS either. The storage
 *     itself is silent; no network wait can explain that. (A deadline that
 *     fires late, on a page the phone paused, proves nothing and gets one
 *     fresh window first: LATE_DEADLINE_SLACK_MS.)
 * The cache answering, at any point, is ALIVE: the save is waiting on the
 * network, and the button says so. The cache silent while IndexedDB answers
 * is UNPROVEN (the commit case above looks exactly like that): no reload and
 * no bar, the button keeps telling the truth, and Sentry hears about it.
 * The probe meeting the b815 assertion or a closing database is CLOSED,
 * handed to the existing recovery as that trigger.
 *
 * WHEN IT RUNS. Never on a timer. On a return to the app after the page was
 * hidden at least RETURN_PROBE_MIN_HIDDEN_MS, and on every restore from the
 * back/forward cache (that one always follows a pagehide, the exact moment
 * restricted mode starts). A shorter trip away reads no cache at all, only
 * the restricted flag, which costs nothing. And when a save somebody tapped
 * has not finished after SLOW_SAVE_MS, then once more if it is still waiting
 * at LONG_SAVE_MS (lib/useSavingText.ts); nothing after that. One probe at a
 * time; an ask while one is running is answered by that one.
 *
 * WHAT A RELOAD DOES TO A SAVE THAT IS STILL WAITING, said plainly:
 *   - Alive (offline or a slow network): no reload happens at all. With the
 *     on-device cache, the SDK wrote the change into its queue in IndexedDB
 *     before it ever tried the network, so it is sent when the signal comes
 *     back, and it survives a reload or a closed app too (proven in the
 *     harness, scenario C). On a phone whose storage is blocked the Hub runs
 *     Firestore in memory, and nothing here reloads that page either.
 *   - Dead: a tap that came AFTER the queue died never reached the SDK's
 *     storage; it lives only in the memory of a page that can never run it.
 *     The reload cannot lose it, because nothing can send it, and the person
 *     taps again on the fresh page, which is exactly what fixed it for
 *     Bobbie. A change the SDK had already written to its on-device queue
 *     before it died survives the reload and the fresh page sends it (proven
 *     in the harness, scenarios A and E).
 *
 * WHAT THIS DOES NOT CATCH, and it may be what Bobbie hit (refute round 1).
 * Restricted mode needs "Safari" in the user agent, and she was in the Home
 * Screen app, whose user agent can leave it out; with such a user agent a
 * real pagehide and back/forward-cache return leave the queue running and
 * this probe says alive. Her case is caught only if her whole IndexedDB went
 * silent, which the harness shows only by a stand-in (scenario E). Two other
 * shapes would look the same to her and are NOT caught: a stuck network
 * connection on a live Firestore (alive), and one lost Firestore storage
 * request while fresh requests still answer (unproven). For both, nothing
 * reloads, the button says Still saving..., the How-To says to close the Hub
 * fully and open it again, and a save still waiting at LONG_SAVE_MS is sent to
 * Sentry once per page as 'firestore-save still waiting'. That message, and
 * 'firestore-liveness unproven', are how anyone learns which shape is real.
 *
 * WHAT THIS FILE IS: the decision with no import from the browser, React or
 * Firebase, so node:test runs it directly. The browser half (the SDK reads,
 * the IndexedDB read, the listeners) is lib/firestoreRecovery.ts; what a dead
 * verdict then does is the existing recovery's decision, unchanged.
 */

// Explicit .ts extension: imported directly by node:test under
// --experimental-strip-types (see firestoreClosedRecovery.ts).
import {
  classifyFirestoreClosed,
  type FirestoreStallChannel,
  type StallProof,
} from './firestoreClosedRecovery.ts';

export type { FirestoreStallChannel, StallProof };

/**
 * A tapped save that has not finished after this long says "Still saving..."
 * and asks the probe once. A Firestore write normally comes back in well
 * under two seconds and a weak signal can stretch that to several, so eight
 * seconds is past an ordinary slow save and still well before a person gives
 * up and swipes the app away. The probe it starts reads only the phone.
 */
export const SLOW_SAVE_MS = 8_000;

/**
 * The same save still waiting after this long is checked once more, and,
 * unless that check proves the database dead, Sentry hears about it once per
 * page (createLivenessSentryGate). A minute is long past any weak signal the
 * first check could have been waiting on, and it is about where Bobbie had
 * already decided the button was stuck. The second check catches a death
 * that came after the first; the report is how anyone ever learns of a save
 * that waited on a database the probe could not call dead (refute round 1).
 */
export const LONG_SAVE_MS = 60_000;

/**
 * The two checks one waiting save gets, each exactly once: onSlow at
 * SLOW_SAVE_MS and onLong at LONG_SAVE_MS. Returns the cancel, which the save
 * finishing calls. Nothing repeats; a save that never finishes is asked
 * about twice and then left to say Still saving... (lib/useSavingText.ts).
 */
export function scheduleSaveChecks(deps: {
  setTimer(run: () => void, ms: number): unknown;
  clearTimer(handle: unknown): void;
  onSlow(): void;
  onLong(): void;
}): () => void {
  const handles = [deps.setTimer(deps.onSlow, SLOW_SAVE_MS), deps.setTimer(deps.onLong, LONG_SAVE_MS)];
  return () => {
    for (const handle of handles) deps.clearTimer(handle);
  };
}

/**
 * A return to the app gets a cache read when the page was hidden at least
 * this long. A phone hides a page for a moment all the time (the share
 * sheet, the photo picker, a glance at a message); ten seconds keeps those
 * from each costing a read while every real trip to another app is checked.
 * A shorter one still has its restricted flag read, which is free, and a
 * dead database it left behind is caught the moment a tap does not finish.
 * A restore from the back/forward cache is always checked, whatever the time.
 */
export const RETURN_PROBE_MIN_HIDDEN_MS = 10_000;

/**
 * How long the one cache read gets. A live Firestore answers it in
 * milliseconds; this is only the point where the probe goes on to ask the
 * storage directly, never a verdict by itself.
 */
export const CACHE_PROBE_TIMEOUT_MS = 5_000;

/** How long the direct IndexedDB read gets before the storage counts as silent. */
export const STORAGE_PROBE_TIMEOUT_MS = 5_000;

/**
 * How late the storage deadline may fire and still be believed. An iPhone
 * pauses a page it hides, and a page paused between the storage ask and its
 * answer runs the overdue deadline first when it wakes, which would call a
 * live storage silent (refute round 1's one untested worry). A deadline that
 * fires later than this proves only that the page was paused, so the storage
 * gets one fresh window and that window decides. Two seconds is past an
 * ordinary busy moment; a longer stall of the main thread also just earns the
 * fresh window, which is the safe way round.
 */
export const LATE_DEADLINE_SLACK_MS = 2_000;

/** The words a save button shows first. */
export const SAVING_TEXT = 'Saving...';

/** What a tapped save says once it has waited SLOW_SAVE_MS. True whichever way it ends. */
export const STILL_SAVING_TEXT = 'Still saving...';

/**
 * What a tapped save says once it has waited AND the bar is up: the database
 * under it is dead, so this save cannot finish on this page. The bar above
 * says what to do.
 */
export const NOT_SAVED_TEXT = 'Not saved yet';

/**
 * The button's words while it is saving. `saving` is the button's own first
 * word, kept until the save has waited SLOW_SAVE_MS.
 */
export function savingLabel(input: { slow: boolean; barVisible: boolean }, saving: string = SAVING_TEXT): string {
  if (!input.slow) return saving;
  return input.barVisible ? NOT_SAVED_TEXT : STILL_SAVING_TEXT;
}

export type LivenessVerdict =
  /** The cache answered: Firestore is running and a save is waiting on the network. */
  | { state: 'alive' }
  /** Proven dead on the phone itself. */
  | { state: 'dead'; proof: StallProof }
  /** The probe met the b815 assertion or a closing database: the existing triggers. */
  | { state: 'closed'; error: unknown }
  /** The cache did not answer but the storage did. Not proof of anything. */
  | { state: 'unproven' }
  /** Nothing to probe: Firestore not started yet, or the Hub shut it down itself (sign-out). */
  | { state: 'skipped' };

/** Everything the probe touches, handed in so a test can fake each one. */
export type LivenessEnv = {
  /** The SDK's own work queue: true in restricted mode, null when it cannot be read. */
  queueRestricted(): boolean | null;
  /** True before Firestore first started, and after the Hub's own terminate() at sign-out. */
  notRunning(): boolean;
  /** One read from Firestore's on-device cache. May throw synchronously (a failed queue does). */
  readCache(): Promise<unknown>;
  /**
   * The browser's IndexedDB, asked directly, outside Firestore. Resolves
   * 'answered' when the storage responded at all (an error is an answer) and
   * 'unavailable' when there is no IndexedDB to ask. Never settling is the
   * silence this exists to see.
   */
  readStorage(): Promise<'answered' | 'unavailable'>;
  setTimer(run: () => void, ms: number): unknown;
  clearTimer(handle: unknown): void;
  /**
   * The wall clock (Date.now), to tell a deadline that fired late from one on
   * time. Without it every deadline counts as on time.
   */
  now?(): number;
};

function fenced<T>(read: () => T, onThrow: T): T {
  try {
    return read();
  } catch {
    return onThrow;
  }
}

/**
 * One probe. Resolves exactly once and never rejects. Every input is fenced:
 * one that throws can only ever cost a verdict short of 'dead', never a
 * surprise reload.
 */
export function probeFirestoreLiveness(env: LivenessEnv): Promise<LivenessVerdict> {
  return new Promise<LivenessVerdict>((resolve) => {
    let settled = false;
    const timers: unknown[] = [];
    const finish = (verdict: LivenessVerdict) => {
      if (settled) return;
      settled = true;
      for (const handle of timers) fenced(() => env.clearTimer(handle), undefined);
      resolve(verdict);
    };
    const restricted = () => fenced(() => env.queueRestricted(), null) === true;

    if (fenced(() => env.notRunning(), true)) {
      finish({ state: 'skipped' });
      return;
    }
    if (restricted()) {
      finish({ state: 'dead', proof: 'queue-restricted' });
      return;
    }

    // The cache read. Any answer at all, now or later, means the queue ran it.
    const onCacheError = (error: unknown) => {
      if (classifyFirestoreClosed(error)) finish({ state: 'closed', error });
      else finish({ state: 'alive' });
    };
    let cacheRead: Promise<unknown>;
    try {
      cacheRead = env.readCache();
    } catch (error) {
      // A failed queue throws its b815 before it even queues the read.
      onCacheError(error);
      return;
    }
    Promise.resolve(cacheRead).then(() => finish({ state: 'alive' }), onCacheError);

    const clock = () => fenced(() => (env.now ? env.now() : Number.NaN), Number.NaN);
    let freshWindowUsed = false;
    const askStorage = () => {
      let storageRead: Promise<unknown>;
      try {
        storageRead = env.readStorage();
      } catch {
        // Could not even ask: that proves nothing either way.
        finish({ state: 'unproven' });
        return;
      }
      // The storage answering while the cache still waits proves only that
      // the queue is held by something, which can be the network. An answer
      // to an earlier ask counts just the same.
      Promise.resolve(storageRead).then(
        () => finish({ state: 'unproven' }),
        () => finish({ state: 'unproven' }),
      );
      const askedAt = clock();
      timers.push(
        env.setTimer(() => {
          if (settled) return;
          if (restricted()) {
            finish({ state: 'dead', proof: 'queue-restricted' });
            return;
          }
          const late = clock() - askedAt - STORAGE_PROBE_TIMEOUT_MS;
          if (!freshWindowUsed && Number.isFinite(late) && late > LATE_DEADLINE_SLACK_MS) {
            // The page was paused, not the storage: ask again, once.
            freshWindowUsed = true;
            askStorage();
            return;
          }
          finish({ state: 'dead', proof: 'storage-silent' });
        }, STORAGE_PROBE_TIMEOUT_MS),
      );
    };

    const startStorageProbe = () => {
      if (settled) return;
      // Restricted mode can begin while the read waits (a pagehide meanwhile).
      if (restricted()) {
        finish({ state: 'dead', proof: 'queue-restricted' });
        return;
      }
      askStorage();
    };
    timers.push(env.setTimer(startStorageProbe, CACHE_PROBE_TIMEOUT_MS));
  });
}

/**
 * Whether a return to the app is worth a cache read. A restore from the
 * back/forward cache always is: it follows a pagehide, which is what starts
 * restricted mode. Otherwise only after a real absence; a page never seen
 * hiding (null) is not a return at all.
 */
export function shouldProbeOnReturn(input: { persisted: boolean; hiddenForMs: number | null }): boolean {
  if (input.persisted) return true;
  if (input.hiddenForMs === null || !Number.isFinite(input.hiddenForMs)) return false;
  return input.hiddenForMs >= RETURN_PROBE_MIN_HIDDEN_MS;
}

/** What a probe found, for Sentry. Never names a person. */
export type LivenessEvent = {
  kind: 'probe';
  channel: FirestoreStallChannel;
  verdict: Exclude<LivenessVerdict['state'], 'skipped'>;
  proof?: StallProof;
};

export type StallMonitorDeps = {
  env: LivenessEnv;
  now(): number;
  /** A proven death: lib/firestoreRecovery.ts hands it to the recovery as 'stalled'. */
  onDead(proof: StallProof, channel: FirestoreStallChannel): void;
  /** The probe met b815 or a closing database: handed to the recovery's detect(). */
  onClosed(error: unknown, channel: FirestoreStallChannel): void;
  report(event: LivenessEvent): void;
};

export type StallMonitor = {
  /** The page was hidden. Remembers when, and nothing else. */
  hidden(): void;
  /** The page is back on screen. A real absence gets the probe; a short one the free flag check. */
  visible(): void;
  /** A restore from the back/forward cache. Always the probe. */
  restored(): void;
  /** A save somebody tapped has waited SLOW_SAVE_MS. */
  slowSave(): void;
  /** The same save is still waiting at LONG_SAVE_MS. */
  longSave(): void;
};

/**
 * When to probe, and where a verdict goes. Making it reads nothing and sets
 * no timer: every probe starts from one of the calls above, and the only
 * timers are the probe's own two deadlines.
 */
export function createStallMonitor(deps: StallMonitorDeps): StallMonitor {
  let hiddenAt: number | null = null;
  let running = false;

  const handle = (verdict: LivenessVerdict, channel: FirestoreStallChannel) => {
    if (verdict.state === 'skipped') return;
    const event: LivenessEvent =
      verdict.state === 'dead'
        ? { kind: 'probe', channel, verdict: 'dead', proof: verdict.proof }
        : { kind: 'probe', channel, verdict: verdict.state };
    fenced(() => deps.report(event), undefined);
    if (verdict.state === 'dead') fenced(() => deps.onDead(verdict.proof, channel), undefined);
    else if (verdict.state === 'closed') fenced(() => deps.onClosed(verdict.error, channel), undefined);
  };

  const probe = (channel: FirestoreStallChannel) => {
    if (running) return;
    running = true;
    let pending: Promise<LivenessVerdict>;
    try {
      pending = probeFirestoreLiveness(deps.env);
    } catch {
      running = false;
      return;
    }
    void pending.then((verdict) => {
      running = false;
      handle(verdict, channel);
    });
  };

  /** The free check: the restricted flag only, no read of anything. */
  const flagOnly = (channel: FirestoreStallChannel) => {
    if (running) return;
    if (fenced(() => deps.env.notRunning(), true)) return;
    if (fenced(() => deps.env.queueRestricted(), null) === true) {
      handle({ state: 'dead', proof: 'queue-restricted' }, channel);
    }
  };

  const now = () => fenced(() => deps.now(), Number.NaN);

  return {
    hidden() {
      const at = now();
      hiddenAt = Number.isFinite(at) ? at : null;
    },
    visible() {
      if (hiddenAt === null) return;
      const away = now() - hiddenAt;
      hiddenAt = null;
      if (shouldProbeOnReturn({ persisted: false, hiddenForMs: away })) probe('return');
      else flagOnly('return');
    },
    restored() {
      hiddenAt = null;
      probe('bfcache');
    },
    slowSave() {
      probe('slow-save');
    },
    longSave() {
      probe('long-save');
    },
  };
}

/** One message for Sentry: what the probe found and where. Never names a person. */
export type LivenessSentryMessage = {
  message: 'firestore-liveness unproven' | 'firestore-save still waiting';
  tags: { firestore_liveness: LivenessEvent['verdict']; firestore_channel: FirestoreStallChannel };
};

/**
 * What a probe verdict sends to Sentry as a message, beyond its breadcrumb,
 * each kind at most once per page (one gate per page):
 *   - 'firestore-liveness unproven': the cache did not answer but the storage
 *     did. No reload, on purpose; counted in case a real death hides there.
 *   - 'firestore-save still waiting': a tapped save still waiting at
 *     LONG_SAVE_MS while the probe calls the database alive or unproven. The
 *     recovery does nothing then, by design, so this is the only trace of a
 *     save it could not help: a stuck connection, or a death this probe
 *     cannot prove.
 * Dead and closed send nothing here: the recovery reports those itself
 * (firestore-closed reload or bar, with trigger, channel, reason and proof).
 */
export function createLivenessSentryGate(): (event: LivenessEvent) => LivenessSentryMessage[] {
  let unprovenSent = false;
  let stillWaitingSent = false;
  return (event) => {
    const out: LivenessSentryMessage[] = [];
    const tags = { firestore_liveness: event.verdict, firestore_channel: event.channel };
    if (event.verdict === 'unproven' && !unprovenSent) {
      unprovenSent = true;
      out.push({ message: 'firestore-liveness unproven', tags });
    }
    if (
      event.channel === 'long-save' &&
      (event.verdict === 'alive' || event.verdict === 'unproven') &&
      !stillWaitingSent
    ) {
      stillWaitingSent = true;
      out.push({ message: 'firestore-save still waiting', tags });
    }
    return out;
  };
}
